/*
 * Copyright (c) 2021 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain proprietary information of OCLC Online Computer
 * Library Center, Inc. and shall not be disclosed in whole or in any part to any third party or used by any person
 * for any purpose, without written consent of OCLC, Inc.  Duplication of any portion of these materials shall include
 * this notice.
 */
import React, { useState, useCallback, useRef } from 'react'
import { useIntl } from 'react-intl'
import { makeStyles } from '@material-ui/core/styles'
import { Box, Card, Typography, CardContent, Button, TextField, CardHeader } from '@material-ui/core'
import AlertNotification from './AlertNotification'
import { changePassword } from '../services/AuthenticationService'
import SuccessCard from './SuccessCard'

const useCustomStyles = makeStyles((theme) => ({
  alertBox: {
    padding: theme.spacing(3, 4, 0),
    [theme.breakpoints.down('xs')]: {
      width: 360,
      margin: 'auto',
      padding: theme.spacing(3, 2.5, 0),
    },
  },
  accountHeader: {
    fontSize: '1.75rem',
    lineHeight: '20px',
    color: theme.palette.bluePrimary.main,
    fontFamily: theme.typography.fontFamily,
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  card: {
    [theme.breakpoints.down('xs')]: {
      height: '100%',
    },
  },
  cardHeader: {
    background: theme.palette.modalHeader,
    borderBottom: `1px solid ${theme.palette.modalGrayBorder}`,
    height: 82,
  },
  cardHolder: {
    width: '378px',
    padding: theme.spacing(6, 0),
    margin: 'auto',
    [theme.breakpoints.down('xs')]: {
      margin: 0,
      width: '100%',
      minWidth: 0,
      padding: 0,
      '& .MuiPaper-rounded': {
        borderRadius: 0,
      },
    },
  },
  cardContent: {
    textAlign: 'left',
    justifyContent: 'center',
    display: 'flex',
    padding: theme.spacing(6, 0, 4),
    '&.MuiCardContent-root:last-child': {
      paddingBottom: theme.spacing(4),
    },
  },
  button: {
    height: 40,
    width: 292,
    textTransform: 'none',
  },
  buttonBox: {
    textAlign: 'center',
  },
  accountLink: {
    display: 'inline-flex',
    justifyContent: 'center',
  },
  inputTextField: {
    width: 292,
  },
  inputTextFieldError: {
    width: 292,
    '& .MuiOutlinedInput-root': {
      border: `2px solid ${theme.palette.notificationError.main}`,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
    },
  },
  label: {
    display: 'block',
    marginBottom: theme.spacing(1),
  },
  flexCenter: {
    display: 'flex',
    justifyContent: 'center',
  },
  newUser: {
    fontWeight: 700,
    alignSelf: 'center',
    paddingRight: theme.spacing(0.5),
  },
  iconFocus: {
    '&:focus': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
}))

/**
 * The footer.
 * @returns {Node} - A react Node.
 */
function ChangePasswordCard() {
  const classes = useCustomStyles()
  const intl = useIntl()
  const oldPasswordRef = useRef()
  const newPasswordRef = useRef()
  const retypePasswordRef = useRef()

  // prettier-ignore
  const messages = {
    currentPasswordLabel: intl.formatMessage({id: 'current_password', defaultMessage: 'Current password'}),
    newPasswordLabel: intl.formatMessage({id: 'new_password', defaultMessage: 'New password'}),
    retypePasswordLabel: intl.formatMessage({id: 'retype_new_password', defaultMessage: 'Re-type new password'}),
    enterOldPassword: intl.formatMessage({id: 'error_no_old_password', defaultMessage: 'Enter current password for your account.'}),
    enterNewPassword: intl.formatMessage({id: 'error_no_new_password', defaultMessage: 'Enter a new password for your account.'}),
    enterRetypePassword: intl.formatMessage({id: 'error_no_new_retype_password', defaultMessage: 'Re-Enter the new password for your account.'}),
    retry: intl.formatMessage({id: 'error_retry', defaultMessage: 'Try again. A system error occurred.'}),
    changePassword: intl.formatMessage({id: 'change_password', defaultMessage: 'Change password'}),
    badPassword: intl.formatMessage({id: 'bad_password_alert', defaultMessage: 'Password contains invalid characters. Use numbers and letters for your password.'}),
    badNewPasswordMatch: intl.formatMessage({id: 'bad_new_password_match_alert', defaultMessage: 'Your new password entries did not match. Try again.'}),
    badOldPasswordMatch: intl.formatMessage({id: 'bad_old_password_match_alert', defaultMessage: 'The current password did not match.'}),
    logout: intl.formatMessage({id: 'logout', defaultMessage: 'Log out'}),
    successChangePassword: intl.formatMessage({id: 'success_change_password', defaultMessage: 'Password successfully changed.'}),
  }

  const [values, setValues] = useState({
    oldPassword: '',
    newPassword: '',
    retypePassword: '',
  })
  const [errors, setErrors] = useState({
    oldPassword: false,
    newPassword: false,
    retypePassword: false,
  })
  const [showAlerts, setShowAlerts] = useState({
    retry: false,
    badPassword: false,
    badOldPasswordMatch: false,
    badNewPasswordMatch: false,
  })
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false)

  const submitForm = useCallback(
    async (e) => {
      e.preventDefault()
      setShowAlerts({ retry: false, badPassword: false, badOldPasswordMatch: false, badNewPasswordMatch: false })
      if (!values.retypePassword) setErrors({ ...errors, retypePassword: true })
      if (!values.newPassword) setErrors({ ...errors, newPassword: true })
      if (!values.oldPassword) setErrors({ ...errors, oldPassword: true })

      if (
        values.oldPassword &&
        values.newPassword &&
        values.retypePassword &&
        values.newPassword === values.retypePassword &&
        !isBasPassword(values.newPassword)
      ) {
        const response = await changePassword(values)
        if (response.status === 200 && response.auth) {
          setChangePasswordSuccess(true)
        } else if (response.status === 401) {
          setShowAlerts({ retry: false, badPassword: false, badOldPasswordMatch: true, badNewPasswordMatch: false })
          setValues({ oldPassword: '', newPassword: '', retypePassword: '' })
          oldPasswordRef.current.value = ''
          newPasswordRef.current.value = ''
          retypePasswordRef.current.value = ''
        } else {
          setShowAlerts({ retry: true, badPassword: false, badOldPasswordMatch: false, badNewPasswordMatch: false })
          setValues({ oldPassword: '', newPassword: '', retypePassword: '' })
          oldPasswordRef.current.value = ''
          newPasswordRef.current.value = ''
          retypePasswordRef.current.value = ''
        }
      } else {
        setShowAlerts({
          retry: false,
          badPassword: isBasPassword(values.newPassword) || isBasPassword(values.retypePassword),
          badNewPasswordMatch: values.newPassword !== values.retypePassword,
          badOldPasswordMatch: false,
        })
      }
    },
    [values.oldPassword, values.newPassword, values.retypePassword],
  )

  const isBasPassword = (password) => {
    return /[^A-Za-z0-9]/g.test(password)
  }

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value })
    if (event.target.value.length > 0) setErrors({ ...errors, [prop]: false })
  }

  const createAlert = (testId, text) => {
    return (
      <Box className={classes.alertBox}>
        <AlertNotification variant='error' id={testId}>
          {text}
        </AlertNotification>
      </Box>
    )
  }

  const createInputError = (testId, text) => {
    return (
      <AlertNotification variant='error' id={testId} alertLevel='inline'>
        {text}
      </AlertNotification>
    )
  }

  const retryAlert = createAlert('change-password-retry-alert', messages.retry)
  const badPasswordAlert = createAlert('bad-password-alert', messages.badPassword)
  const badOldPasswordMatchAlert = createAlert('bad-old-password-match-alert', messages.badOldPasswordMatch)
  const badNewPasswordMatchAlert = createAlert('bad-new-password-match-alert', messages.badNewPasswordMatch)
  const activeAlerts = [
    showAlerts.retry ? retryAlert : null,
    showAlerts.badPassword ? badPasswordAlert : null,
    showAlerts.badOldPasswordMatch ? badOldPasswordMatchAlert : null,
    showAlerts.badNewPasswordMatch ? badNewPasswordMatchAlert : null,
  ]
  const noOldPasswordError = createInputError('no-old-password-error', messages.enterOldPassword)
  const noNewPasswordError = createInputError('no-new-password-error', messages.enterNewPassword)
  const noRetypePasswordError = createInputError('no-retype-password-error', messages.enterRetypePassword)

  const changePasswordForm = (
    <>
      <form id='change-password-form' data-testid='change-password-form'>
        <Box mb={2.5}>
          <Typography component='label' variant='body2' htmlFor='change-password-old' className={classes.label}>
            <strong>{messages.currentPasswordLabel}</strong>
          </Typography>
          <TextField
            inputRef={oldPasswordRef}
            className={errors.oldPassword ? classes.inputTextFieldError : classes.inputTextField}
            id='change-password-old'
            data-testid='change-password-old'
            size='small'
            variant='outlined'
            fullWidth
            color={errors.oldPassword ? 'secondary' : 'primary'}
            required
            defaultValue={''}
            inputProps={{ maxLength: 25 }}
            onChange={handleChange('oldPassword')}
            type='password'
          />
          {errors.oldPassword ? noOldPasswordError : null}
        </Box>
        <Box mb={2.5}>
          <Typography component='label' variant='body2' htmlFor='change-password-new' className={classes.label}>
            <strong>{messages.newPasswordLabel}</strong>
          </Typography>
          <TextField
            inputRef={newPasswordRef}
            className={errors.newPassword ? classes.inputTextFieldError : classes.inputTextField}
            id='change-password-new'
            data-testid='change-password-new'
            size='small'
            variant='outlined'
            fullWidth
            color={errors.newPassword ? 'secondary' : 'primary'}
            required
            defaultValue={''}
            inputProps={{ maxLength: 25 }}
            onChange={handleChange('newPassword')}
            type='password'
          />
          {errors.newPassword ? noNewPasswordError : null}
        </Box>
        <Box mb={4}>
          <Typography component='label' variant='body2' htmlFor='change-password-retype' className={classes.label}>
            <strong>{messages.retypePasswordLabel}</strong>
          </Typography>
          <TextField
            inputRef={retypePasswordRef}
            className={errors.retypePassword ? classes.inputTextFieldError : classes.inputTextField}
            id='change-password-retype'
            data-testid='change-password-retype'
            size='small'
            variant='outlined'
            fullWidth
            color={errors.retypePassword ? 'secondary' : 'primary'}
            required
            defaultValue={''}
            inputProps={{ maxLength: 25 }}
            onChange={handleChange('retypePassword')}
            type='password'
          />
          {errors.retypePassword ? noRetypePasswordError : null}
        </Box>
        <Box mb={2} className={classes.buttonBox}>
          <Button
            color='primary'
            variant='contained'
            className={classes.button}
            type='submit'
            data-testid='change-password-cta'
            onClick={submitForm}>
            {messages.changePassword}
          </Button>
        </Box>
      </form>
    </>
  )

  const cardHeaderTitle = (
    <Typography
      variant='h1'
      data-testid='change-password-card-header'
      underline='none'
      className={classes.accountHeader}>
      {messages.changePassword}
    </Typography>
  )
  const successCard = (
    <SuccessCard
      title={messages.changePassword}
      buttonText={messages.logout}
      boldBodyText={messages.successChangePassword}
      isLogout
    />
  )

  return changePasswordSuccess ? (
    successCard
  ) : (
    <Box className={classes.cardHolder}>
      <Card elevation={9} className={classes.card}>
        <CardHeader className={classes.cardHeader} title={cardHeaderTitle} />
        {activeAlerts.map((alert) => {
          if (alert) return <React.Fragment key={activeAlerts.indexOf(alert)}>{alert}</React.Fragment>
        })}
        <CardContent className={classes.cardContent}>{changePasswordForm}</CardContent>
      </Card>
    </Box>
  )
}

export default ChangePasswordCard
